import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  OperationCanal,
  OperationCategorie,
  OperationRow,
  OperationSens,
  OperationStatut,
} from '@/lib/database.types';

export interface OperationsFilter {
  entrepriseId?: string | null;
  compteBancaireId?: string | null;
  caisseId?: string | null;
  canal?: OperationCanal | null;
  sens?: OperationSens | null;
  categorie?: OperationCategorie | null;
  statut?: OperationStatut | null;
  tierId?: string | null;
  activiteId?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  amountMin?: number | null;
  amountMax?: number | null;
  search?: string;
  page?: number;
  pageSize?: number;
}

export function useOperations(filter: OperationsFilter) {
  const pageSize = filter.pageSize ?? 50;
  const page = filter.page ?? 0;
  return useQuery({
    queryKey: ['operations', filter],
    queryFn: async (): Promise<{ rows: OperationRow[]; count: number }> => {
      let q = supabase
        .from('operations')
        .select('*', { count: 'exact' })
        .order('date_operation', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (filter.entrepriseId) q = q.eq('entreprise_id', filter.entrepriseId);
      if (filter.compteBancaireId) q = q.eq('compte_bancaire_id', filter.compteBancaireId);
      if (filter.caisseId) q = q.eq('caisse_id', filter.caisseId);
      if (filter.canal) q = q.eq('canal', filter.canal);
      if (filter.sens) q = q.eq('sens', filter.sens);
      if (filter.categorie) q = q.eq('categorie', filter.categorie);
      if (filter.statut) q = q.eq('statut', filter.statut);
      if (filter.tierId) q = q.eq('tier_id', filter.tierId);
      if (filter.activiteId) q = q.eq('activite_id', filter.activiteId);
      if (filter.dateFrom) q = q.gte('date_operation', filter.dateFrom);
      if (filter.dateTo) q = q.lte('date_operation', filter.dateTo);
      if (filter.amountMin !== null && filter.amountMin !== undefined)
        q = q.gte('montant_dzd', filter.amountMin);
      if (filter.amountMax !== null && filter.amountMax !== undefined)
        q = q.lte('montant_dzd', filter.amountMax);
      if (filter.search && filter.search.length >= 2) {
        const s = filter.search.replace(/%/g, '');
        q = q.or(`reference.ilike.%${s}%,motif.ilike.%${s}%,observation.ilike.%${s}%`);
      }
      const { data, error, count } = await q;
      if (error) throw error;
      return { rows: data, count: count ?? 0 };
    },
    placeholderData: (prev) => prev,
  });
}

export interface UpsertOperationInput {
  id?: string;
  entreprise_id: string;
  date_operation?: string;
  sens: OperationSens;
  canal: OperationCanal;
  categorie?: OperationCategorie;
  caisse_id?: string | null;
  compte_bancaire_id?: string | null;
  tier_id?: string | null;
  activite_id?: string | null;
  /** Sprint 6.7 — activité qui doit porter cette charge/recette analytiquement */
  activite_beneficiaire_id?: string | null;
  /** Sprint 6.7 — activité depuis laquelle l'argent est physiquement payé/encaissé */
  activite_emettrice_id?: string | null;
  /** Sprint 6.8.1 — snapshot coordonnées tier */
  contact_telephone?: string | null;
  contact_email?: string | null;
  contact_adresse?: string | null;
  bon_recu_ref?: string | null;
  service_id?: string | null;
  devise_code?: string;
  montant: number;
  taux_change?: number;
  montant_dzd: number;
  motif: string;
  observation?: string | null;
  justificatif_id?: string | null;
  statut?: OperationStatut;
  created_by?: string | null;
}

export function useUpsertOperation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertOperationInput): Promise<OperationRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('operations')
          .update(rest as never)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as OperationRow;
      }
      const { data, error } = await supabase
        .from('operations')
        .insert(rest as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as OperationRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
      void qc.invalidateQueries({ queryKey: ['treso_evolution'] });
    },
  });
}

export function useUpdateOperationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; statut: OperationStatut }): Promise<void> => {
      const now = new Date().toISOString();
      const patch = {
        statut: input.statut,
        ...(input.statut === 'validee' ? { validated_at: now } : {}),
        ...(input.statut === 'approuvee' ? { approved_at: now } : {}),
        ...(input.statut === 'comptabilisee' ? { comptabilisee_at: now } : {}),
        ...(input.statut === 'annulee' ? { cancelled_at: now } : {}),
      };
      const { error } = await supabase.from('operations').update(patch).eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
      void qc.invalidateQueries({ queryKey: ['treso_evolution'] });
    },
  });
}

export function useContrePassation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (operationId: string): Promise<string> => {
      const { data, error } = await supabase.rpc('operations_contre_passation', {
        p_operation_id: operationId,
      });
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}

export function useDeleteOperation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      // Sprint 6.9.6 — `.select('id')` après delete pour détecter les rejets RLS
      // silencieux (PostgREST renvoie 200 OK avec 0 ligne quand la policy refuse).
      const { data, error } = await supabase.from('operations').delete().eq('id', id).select('id');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error(
          'Suppression refusée par RLS — opération comptabilisée (passez par contre-passation) ou rôle insuffisant.',
        );
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}

/**
 * Bulk operations: chunked + parallelized for large selections.
 * Returns counts of success/errors per ID.
 */
export interface BulkResult {
  success: string[];
  errors: { id: string; message: string }[];
}

export function useBulkUpdateOperationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[]; statut: OperationStatut }): Promise<BulkResult> => {
      const now = new Date().toISOString();
      const patch = {
        statut: input.statut,
        ...(input.statut === 'validee' ? { validated_at: now } : {}),
        ...(input.statut === 'approuvee' ? { approved_at: now } : {}),
        ...(input.statut === 'comptabilisee' ? { comptabilisee_at: now } : {}),
        ...(input.statut === 'annulee' ? { cancelled_at: now } : {}),
      };
      const result: BulkResult = { success: [], errors: [] };
      // Single UPDATE WHERE id IN (chunk) for performance
      const chunks: string[][] = [];
      for (let i = 0; i < input.ids.length; i += 200) chunks.push(input.ids.slice(i, i + 200));
      for (const chunk of chunks) {
        const { error, data } = await supabase
          .from('operations')
          .update(patch)
          .in('id', chunk)
          .select('id');
        if (error) {
          for (const id of chunk) result.errors.push({ id, message: error.message });
        } else {
          for (const r of data ?? []) result.success.push(r.id);
        }
      }
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}

export function useBulkDeleteOperations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]): Promise<BulkResult> => {
      const result: BulkResult = { success: [], errors: [] };
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200));
      for (const chunk of chunks) {
        const { error, data } = await supabase
          .from('operations')
          .delete()
          .in('id', chunk)
          .select('id');
        if (error) {
          for (const id of chunk) result.errors.push({ id, message: error.message });
        } else {
          for (const r of data ?? []) result.success.push(r.id);
        }
      }
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operations'] });
    },
  });
}

export function useBulkContrePassation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]): Promise<BulkResult> => {
      const result: BulkResult = { success: [], errors: [] };
      // RPC ne supporte pas le batch → on appelle en parallèle
      await Promise.all(
        ids.map(async (id) => {
          const { error } = await supabase.rpc('operations_contre_passation', {
            p_operation_id: id,
          });
          if (error) result.errors.push({ id, message: error.message });
          else result.success.push(id);
        }),
      );
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}

export function useBulkInsertOperations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: UpsertOperationInput[]): Promise<OperationRow[]> => {
      const { data, error } = await supabase
        .from('operations')
        .insert(rows as never)
        .select('*');
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
    },
  });
}
